"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { rhfResolver } from "@/lib/form";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { loginSchema, type LoginInput, type PublicSettings } from "@plexstore/types";

import { DiscordIcon } from "@/components/shared/icons";
import { Field, FormError } from "@/components/shared/form-field";
import { GlassCard } from "@/components/shared/glass-card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { ApiError, errorMessage } from "@/lib/api/errors";
import { authService } from "@/services/client";

interface LoginFormProps {
  auth: PublicSettings["auth"];
  storeName: string;
  returnTo: string;
  initialError: string | null;
}

export function LoginForm({ auth, storeName, returnTo, initialError }: LoginFormProps) {
  const router = useRouter();
  const [needsVerification, setNeedsVerification] = useState<string | null>(null);
  const form = useForm<LoginInput>({ resolver: rhfResolver(loginSchema), defaultValues: { email: "", password: "", remember: false } });
  const login = useMutation({
    mutationFn: (values: LoginInput) => authService.login(values),
    onSuccess: () => {
      router.push(returnTo);
      router.refresh();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "EMAIL_NOT_VERIFIED") setNeedsVerification(form.getValues("email"));
    },
  });
  const resend = useMutation({ mutationFn: (email: string) => authService.resendVerification(email), onSuccess: (r) => toast.success(r.message), onError: (e) => toast.error(errorMessage(e)) });

  const discordHref = `/auth/discord?returnTo=${encodeURIComponent(returnTo)}`;

  return (
    <GlassCard className="rounded-3xl p-8">
      <h1 className="text-2xl font-bold">Welcome back</h1>
      <p className="mt-1 text-sm text-text-secondary">Sign in to {storeName} to access your purchases.</p>

      <FormError message={initialError} />

      {auth.discordEnabled && (
        <Button asChild size="lg" className="mt-6 h-12 w-full rounded-xl border-0 bg-discord text-base font-semibold text-white hover:bg-[#4752c4]">
          <a href={discordHref}>
            <DiscordIcon className="size-5" /> Continue with Discord
          </a>
        </Button>
      )}

      {auth.discordEnabled && auth.localEnabled && (
        <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-wider text-text-muted">
          <span className="h-px flex-1 bg-white/10" /> or <span className="h-px flex-1 bg-white/10" />
        </div>
      )}

      {auth.localEnabled && (
        <form onSubmit={form.handleSubmit((v) => login.mutate(v))} className="space-y-4">
          <Field label="Email" htmlFor="email" required error={form.formState.errors.email?.message}>
            <Input id="email" type="email" autoComplete="email" {...form.register("email")} className="h-11 rounded-xl" />
          </Field>
          <Field label="Password" htmlFor="password" required error={form.formState.errors.password?.message}>
            <Input id="password" type="password" autoComplete="current-password" {...form.register("password")} className="h-11 rounded-xl" />
          </Field>
          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center gap-2 text-text-secondary">
              <Checkbox checked={form.watch("remember")} onCheckedChange={(v) => form.setValue("remember", v === true)} /> Remember me
            </label>
            <Link href="/forgot-password" className="text-brand hover:underline">
              Forgot password?
            </Link>
          </div>
          {login.error && !(login.error instanceof ApiError && login.error.code === "EMAIL_NOT_VERIFIED") && <FormError message={errorMessage(login.error)} />}
          {needsVerification && (
            <div className="rounded-xl border border-gold/30 bg-gold/10 p-4 text-sm text-gold">
              Please verify your email before logging in.{" "}
              <button type="button" className="font-semibold underline" onClick={() => resend.mutate(needsVerification)} disabled={resend.isPending}>
                Resend verification email
              </button>
            </div>
          )}
          <Button type="submit" size="lg" className="h-12 w-full rounded-xl text-base font-semibold accent-glow" disabled={login.isPending}>
            {login.isPending ? "Signing in…" : "Sign in"}
          </Button>
          <p className="text-center text-sm text-text-secondary">
            No account yet?{" "}
            <Link href="/register" className="text-brand hover:underline">
              Create one
            </Link>
          </p>
        </form>
      )}

      {!auth.discordEnabled && !auth.localEnabled && <FormError message="Sign-in is currently disabled. Please contact the store owner." />}
    </GlassCard>
  );
}
